import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupLayout from "../SetupLayout/SetupLayout";
import { useSetupState, useSetupDispatch, addRole, resetSetup } from "../../../auth/SetupContext";
import { API_BASE_URL } from "../../../auth/config";
import formStyles from "../setupForm.module.scss";

export default function CreateRoles() {
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();
  const { companyId, roles } = useSetupState();

  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) navigate("/setup/company", { replace: true });
  }, [companyId, navigate]);

  const handleBack = () => {
    navigate("/", { replace: true });
    setTimeout(() => dispatch(resetSetup()), 0);
  };

  const validateRoleName = (value) => (value.trim() ? "" : "Role name is required.");

  const isFormValid = !validateRoleName(roleName);

  const handleRoleNameChange = (e) => {
    const { value } = e.target;
    setRoleName(value);
    setRoleNameError(validateRoleName(value));
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    const validationError = validateRoleName(roleName);
    setRoleNameError(validationError);
    if (validationError) return;

    setError("");
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/roles`, { name: roleName, companyId });
      dispatch(addRole(res.data));
      setRoleName("");
      setRoleNameError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create role");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (roles.length === 0) {
      setError("Add at least one role before continuing.");
      return;
    }
    navigate("/setup/user", { replace: true });
  };

  return (
    <SetupLayout
      step={4}
      totalSteps={6}
      title="Create roles"
      subtitle="Add the roles users at this company can be assigned."
      error={error}
      onBack={handleBack}
      backDisabled={isSubmitting}
      loading={isSubmitting}
      loadingText="Saving role..."
    >
      <form onSubmit={handleAddRole} className={formStyles.row}>
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} htmlFor="roleName">Role name</label>
          <input
            id="roleName"
            className={formStyles.input}
            value={roleName}
            onChange={handleRoleNameChange}
            placeholder="e.g. Admin"
          />
          {roleNameError && <span className={formStyles.fieldError}>{roleNameError}</span>}
        </div>
        <div className={formStyles.btnRow} style={{ alignSelf: "flex-end" }}>
          <button type="submit" className={formStyles.secondaryBtn} disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? "Adding..." : "Add role"}
          </button>
        </div>
      </form>

      {roles.length > 0 && (
        <div className={formStyles.roleList}>
          {roles.map((role) => (
            <span key={role.id ?? role.name} className={formStyles.roleChip}>
              {role.name}
            </span>
          ))}
        </div>
      )}

      <div className={formStyles.btnRow}>
        <button type="button" className={formStyles.primaryBtn} onClick={handleContinue}>
          Continue
        </button>
      </div>
    </SetupLayout>
  );
}
